import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectClinicId } from "../../../store/slices/authSlice";
import {
  setSearchQuery,
  setStatusFilter,
  setCurrentPage,
  selectSearchQuery,
  selectStatusFilter,
  selectCurrentPage,
} from "../../../store/slices/patientsSlice";
import { useGetPatientsQuery } from "../../../store/services/patientsApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import PatientsTable from "../../../components/patients/PatientsTable";
import PatientsCards from "../../../components/patients/PatientsCards";
import ViewToggle from "../../../components/ui/ViewToggle";
import {
  MagnifyingGlass,
  Funnel,
  UsersThree,
  User,
  Eye,
} from "@phosphor-icons/react";
import { HelpButton } from "../../../components/shared";
import "./AdminPatientsPage.css";

const STATUS_OPTIONS = [
  { value: "all", label: "כל הסטטוסים" },
  { value: "active", label: "פעיל" },
  { value: "waiting", label: "בהמתנה" },
  { value: "finished", label: "סיים טיפול" },
  { value: "initial", label: "פנייה ראשונית" },
];

// Virtual therapist-filter value: patients whose primary therapist is missing
// or deactivated (E9). Resolved client-side over the full clinic list.
const INACTIVE_THERAPIST_FILTER = "inactive-therapist";

const VIEW_STORAGE_KEY = "clinikit_admin_patients_view";
const MOBILE_BREAKPOINT = 768;
// Client-side pagination: fetch the full (bounded) list once, slice locally.
// Cursor-based paging was removed — it produced duplicate/skipped patients on
// page boundaries and made a true total count impossible.
const PATIENTS_PER_PAGE = 50;
const PATIENTS_FETCH_LIMIT = 1000;

function AdminPatientsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const searchQuery = useSelector(selectSearchQuery);
  const statusFilter = useSelector(selectStatusFilter);
  const currentPage = useSelector(selectCurrentPage);

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [selectedTherapist, setSelectedTherapist] = useState("all");
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );

  // Initialize view from localStorage, default to 'table' (or 'cards' on mobile)
  const [viewMode, setViewMode] = useState(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return "cards";
    }
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    return saved === "cards" ? "cards" : "table";
  });

  // Listen to window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Force cards view on mobile
      if (mobile && viewMode === "table") {
        setViewMode("cards");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Save view preference to localStorage
  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem(VIEW_STORAGE_KEY, newView);
  };

  // Get all therapists in the clinic
  const { data: therapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Get ALL patients (no therapistId filter = admin view)
  // or filtered by selected therapist
  const { data, isLoading, error } = useGetPatientsQuery(
    {
      clinicId,
      therapistId:
        selectedTherapist === "all" ||
        selectedTherapist === INACTIVE_THERAPIST_FILTER
          ? undefined
          : selectedTherapist,
      status: statusFilter,
      pageSize: PATIENTS_FETCH_LIMIT,
    },
    { skip: !clinicId }
  );

  // Create a map of therapist names for display
  const therapistMap = useMemo(() => {
    const map = {};
    therapists.forEach((t) => {
      map[t.uid || t.id] = `${t.firstName} ${t.lastName}`;
    });
    return map;
  }, [therapists]);

  // Add therapist name(s) to patients for display
  const patientsWithTherapist = useMemo(() => {
    if (!data?.patients) return [];
    return data.patients.map((patient) => {
      const ids = patient.therapistIds?.length > 0
        ? patient.therapistIds
        : patient.therapistId ? [patient.therapistId] : [];
      const names = ids
        .map((id) => therapistMap[id])
        .filter(Boolean);
      return {
        ...patient,
        therapistName: names.length > 0 ? names.join(", ") : "לא ידוע",
      };
    });
  }, [data?.patients, therapistMap]);

  // Therapist ids that can still own patients (E9: everything else is "inactive")
  const activeTherapistIds = useMemo(
    () =>
      new Set(
        therapists
          .filter((t) => t.isActive !== false)
          .map((t) => t.uid || t.id)
      ),
    [therapists]
  );

  // Filter patients based on search (client-side for better UX)
  const filteredPatients = useMemo(() => {
    if (!patientsWithTherapist) return [];
    let base = patientsWithTherapist;
    if (selectedTherapist === INACTIVE_THERAPIST_FILTER && therapists.length > 0) {
      base = base.filter(
        (p) => !p.therapistId || !activeTherapistIds.has(p.therapistId)
      );
    }
    if (!localSearch) return base;

    const searchLower = localSearch.toLowerCase();
    return base.filter(
      (p) =>
        p.firstName?.toLowerCase().includes(searchLower) ||
        p.lastName?.toLowerCase().includes(searchLower) ||
        p.phone?.includes(localSearch) ||
        p.therapistName?.toLowerCase().includes(searchLower)
    );
  }, [
    patientsWithTherapist,
    localSearch,
    selectedTherapist,
    therapists.length,
    activeTherapistIds,
  ]);

  // Group patients by therapist for stats (multi-therapist aware:
  // a patient with several therapists counts for each of them)
  const patientsByTherapist = useMemo(() => {
    const grouped = {};
    (data?.patients || []).forEach((patient) => {
      const ids =
        patient.therapistIds?.length > 0
          ? patient.therapistIds
          : [patient.therapistId || "unknown"];
      ids.forEach((id) => {
        if (!grouped[id]) {
          grouped[id] = [];
        }
        grouped[id].push(patient);
      });
    });
    return grouped;
  }, [data?.patients]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPatients.length / PATIENTS_PER_PAGE)
  );

  // Clamp current page if the list shrank (deletion, filter change, navigation)
  useEffect(() => {
    if (!isLoading && data && currentPage > totalPages) {
      dispatch(setCurrentPage(totalPages));
    }
  }, [isLoading, data, currentPage, totalPages, dispatch]);

  const pagePatients = useMemo(
    () =>
      filteredPatients.slice(
        (currentPage - 1) * PATIENTS_PER_PAGE,
        currentPage * PATIENTS_PER_PAGE
      ),
    [filteredPatients, currentPage]
  );

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
    if (currentPage !== 1) dispatch(setCurrentPage(1));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    dispatch(setSearchQuery(localSearch));
  };

  const handleStatusChange = (e) => {
    dispatch(setStatusFilter(e.target.value));
  };

  const handleTherapistChange = (e) => {
    setSelectedTherapist(e.target.value);
    dispatch(setCurrentPage(1)); // Reset to first page
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1 || page > totalPages) return;
      dispatch(setCurrentPage(page));
    },
    [totalPages, dispatch]
  );

  const handleViewPatient = (patientId) => {
    navigate(`/dashboard/patients/${patientId}?view=admin`);
  };

  // Stats (true totals — the full list is fetched)
  const totalPatients = data?.patients?.length || 0;
  const activeTherapists = Object.keys(patientsByTherapist).length;

  return (
    <div className="admin-patients-page">
      <div className="admin-patients-header">
        <div className="header-title">
          <h1>
            <UsersThree size={28} weight="bold" />
            <span>ניהול מטופלים</span>
          </h1>
          <p className="subtitle">
            צפייה בכל המטופלים בקליניקה
          </p>
        </div>

        <div className="header-left-group">
          <HelpButton featureKey="admin_patients" />
          <div className="header-stats">
            <div className="stat-item">
              <UsersThree size={20} weight="bold" />
              <span className="stat-value">{totalPatients}</span>
              <span className="stat-label">מטופלים</span>
            </div>
            <div className="stat-item">
              <User size={20} weight="bold" />
              <span className="stat-value">{activeTherapists}</span>
              <span className="stat-label">מטפלים פעילים תחת הקליניקה</span>
              <span className="stat-sublabel">בנוסף אליך</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-patients-filters">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper">
            <MagnifyingGlass
              size={18}
              weight="regular"
              className="search-icon"
            />
            <input
              type="text"
              placeholder="חיפוש לפי שם, טלפון או מטפל..."
              value={localSearch}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </form>

        <div className="filters-right">
          {/* Therapist Filter */}
          <div className="filter-wrapper therapist-filter">
            <User size={18} weight="regular" className="filter-icon" />
            <select
              value={selectedTherapist}
              onChange={handleTherapistChange}
              className="status-filter"
            >
              <option value="all">כל המטפלים</option>
              <option value={INACTIVE_THERAPIST_FILTER}>
                מטופלים ללא מטפל/ת פעיל/ה
              </option>
              {therapists.map((therapist) => (
                <option key={therapist.uid || therapist.id} value={therapist.uid || therapist.id}>
                  {therapist.firstName} {therapist.lastName}
                  {patientsByTherapist[therapist.uid || therapist.id]
                    ? ` (${patientsByTherapist[therapist.uid || therapist.id].length})`
                    : " (0)"}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="filter-wrapper">
            <Funnel size={18} weight="regular" className="filter-icon" />
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="status-filter"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <ViewToggle
            view={viewMode}
            onViewChange={handleViewChange}
            disableTable={isMobile}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          שגיאה בטעינת מטופלים: {error.message}
        </div>
      )}

      {/* Info banner for admin view */}
      <div className="admin-info-banner">
        <Eye size={18} weight="bold" />
        <span>מצב צפייה בלבד - ניתן לצפות בפרטי מטופלים אך לא לערוך</span>
      </div>

      {viewMode === "table" ? (
        <PatientsTable
          patients={pagePatients}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          onRowClick={handleViewPatient}
          showTherapist={true}
          isAdminView={true}
          hasFilters={!!localSearch || statusFilter !== "all" || selectedTherapist !== "all"}
        />
      ) : (
        <PatientsCards
          patients={pagePatients}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          onCardClick={handleViewPatient}
          showTherapist={true}
          isAdminView={true}
          hasFilters={!!localSearch || statusFilter !== "all" || selectedTherapist !== "all"}
        />
      )}
    </div>
  );
}

export default AdminPatientsPage;
